/* screens/settings/BusinessRestaurantTab.jsx — профиль + реквизиты ресторана бизнес-ws.
   Профиль (LLM-контекст, system-prompt) переиспользует общий ProfileTab. Ниже — отдельная
   панель «Реквизиты» (юр. название/ИНН/адрес/руководитель) для печатных форм ТТК
   (GET/PATCH /api/auth/workspace/requisites/, scoped по wsId → X-Workspace-Id; PATCH — только админ).
   ХЕНДОФФ → frontend/pages/settings/BusinessRestaurantTab.jsx. */

import React from 'react';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, TextInput } from '../../components/ui/Field.jsx';
import { hooks } from '../../hooks/index.jsx';
import { restaurantReal } from '../../lib/adapters/restaurant.js';
import { ProfileTab } from '../restaurant/ProfileTab.jsx';

  const { useState, useEffect } = React;
  const { useFakeQuery } = hooks;

  const REQ_FIELDS = [
    { key: 'legal_name', label: 'Юридическое название', full: true, placeholder: 'ООО «…»' },
    { key: 'inn', label: 'ИНН', placeholder: '10 или 12 цифр' },
    { key: 'phone', label: 'Телефон', placeholder: '+7 …' },
    { key: 'address', label: 'Адрес', full: true, placeholder: 'Город, улица, дом' },
    { key: 'director_name', label: 'Руководитель', placeholder: 'ФИО' },
    { key: 'director_position', label: 'Должность', placeholder: 'Генеральный директор' },
    { key: 'display_name', label: 'Отображаемое название', full: true, placeholder: 'Имя на печатных формах' },
  ];

  function RequisitesForm({ data, wsId }) {
    const toast = useToast();
    const [form, setForm] = useState(data);
    const [busy, setBusy] = useState(false);
    useEffect(() => { setForm(data); }, [data]);

    const save = async () => {
      setBusy(true);
      try {
        const saved = await restaurantReal.updateRequisites(form, wsId);
        setForm(saved);
        toast.show('Реквизиты сохранены', 'success');
      } catch (e) {
        toast.show(e.code === 'not_admin'
          ? 'Реквизиты может править только администратор'
          : 'Не удалось сохранить реквизиты', 'danger');
      } finally { setBusy(false); }
    };

    return (
      <section className="bill-section stack stack-16">
        <div className="bill-section__head"><h3 className="t-h4">Реквизиты</h3></div>
        <p className="t-body-sm text-mute">Подставляются в печатные формы ТТК: шапка «УТВЕРЖДАЮ», юридическое название, ИНН.</p>
        <div className="profile-grid">
          {REQ_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} className={f.full ? 'profile-grid__full' : undefined}>
              <TextInput value={form[f.key] || ''} placeholder={f.placeholder}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            </Field>
          ))}
        </div>
        <div className="cluster cluster-4" style={{ justifyContent: 'flex-end' }}>
          <Button variant="primary" loading={busy} disabled={busy} onClick={save}>Сохранить реквизиты</Button>
        </div>
      </section>
    );
  }

  function RequisitesPanel({ wsId }) {
    const q = useFakeQuery('biz-requisites:' + (wsId == null ? '' : wsId), () => restaurantReal.getRequisites(wsId), [wsId]);
    return (
      <QueryBoundary loading={q.loading} error={q.error} onRetry={q.reload}
        skeleton={<span className="skeleton" style={{ height: 240, borderRadius: 'var(--radius-lg)', display: 'block' }} />}>
        {q.data && <RequisitesForm data={q.data} wsId={wsId} />}
      </QueryBoundary>
    );
  }

  function BusinessRestaurantTab({ wsId }) {
    const q = useFakeQuery('biz-profile:' + (wsId == null ? '' : wsId), () => restaurantReal.getProfile(wsId), [wsId]);
    return (
      <div className="stack stack-24">
        <QueryBoundary loading={q.loading} error={q.error} onRetry={q.reload}
          skeleton={<div className="profile-form stack stack-16">{[0, 1, 2].map((i) => <span key={i} className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />)}</div>}>
          {q.data && <ProfileTab profile={q.data} onSave={(form) => restaurantReal.updateProfile(form, wsId)}
            onUploadPhoto={(f) => restaurantReal.uploadProfilePhoto(f, wsId)}
            onDeletePhoto={() => restaurantReal.deleteProfilePhoto(wsId)} />}
        </QueryBoundary>
        <RequisitesPanel wsId={wsId} />
      </div>
    );
  }

export { BusinessRestaurantTab };
